(() => {
  const selector = '[data-scent-studio]';

  const escapeHtml = (value = '') => String(value).replace(/[&<>"']/g, character => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;'
  })[character]);

  const optionsFrom = element => (element?.dataset.options || '')
    .split(/\r?\n|\|/)
    .map(option => option.trim())
    .filter(Boolean);

  class ScentStudio {
    constructor(root) {
      this.root = root;
      this.steps = [...root.querySelectorAll('[data-studio-step]')];
      this.config = JSON.parse(root.querySelector('[data-studio-config]').textContent);
      this.storageKey = `scent-studio:${root.dataset.sectionId}`;
      this.state = this.restore() || { index: 0, answers: {} };
      this.state.index = Math.min(this.state.index || 0, Math.max(this.steps.length - 1, 0));
      this.buildOptions();
      this.restoreFields();
      this.bind();
      this.go(this.state.index);
    }

    restore() {
      if (this.root.dataset.saveProgress !== 'true') return null;
      try { return JSON.parse(sessionStorage.getItem(this.storageKey)); } catch (_) { return null; }
    }

    save() {
      if (this.root.dataset.saveProgress !== 'true') return;
      try { sessionStorage.setItem(this.storageKey, JSON.stringify(this.state)); } catch (_) {}
    }

    buildOptions() {
      this.root.querySelectorAll('[data-options]').forEach(container => {
        const multiple = container.closest('[data-multiple]') || container.matches('[data-multiple]');
        const className = multiple ? 'scent-studio__chip' : 'scent-studio__pill';
        container.innerHTML = optionsFrom(container).map(option =>
          `<button type="button" class="${className}" data-option="${escapeHtml(option)}" aria-pressed="false">${escapeHtml(option)}</button>`
        ).join('');
      });
    }

    restoreFields() {
      this.root.querySelectorAll('[data-field]').forEach(field => {
        field.value = this.state.answers[field.dataset.field] || '';
      });
      this.root.querySelectorAll('[data-single]').forEach(group => {
        const value = this.state.answers[group.dataset.single];
        group.querySelectorAll('[data-option]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.option === value)));
        this.updateConcierge(group, value);
      });
      this.root.querySelectorAll('[data-multiple]').forEach(group => {
        const stored = this.state.answers[group.dataset.multiple] || [];
        const values = Array.isArray(stored) ? stored : String(stored).split(',').map(value => value.trim());
        group.querySelectorAll('[data-option]').forEach(button => button.setAttribute('aria-pressed', String(values.includes(button.dataset.option))));
        const customField = group.closest('[data-studio-step]')?.querySelector(`[data-custom-for="${group.dataset.multiple}"]`);
        if (customField) {
          const customValue = this.state.answers[`${group.dataset.multiple}_custom`] || '';
          customField.value = [...values, customValue].filter(Boolean).join(', ');
        }
        this.updateMultiConcierge(group);
      });
      this.toggleSpecificSites();
    }

    bind() {
      this.root.addEventListener('click', event => {
        const next = event.target.closest('[data-next]');
        if (next) return this.next(next);
        if (event.target.closest('[data-back]')) return this.go(this.state.index - 1);
        if (event.target.closest('[data-skip]')) return this.skip();
        if (event.target.closest('[data-side-prev]')) return this.go(this.state.index - 1);
        if (event.target.closest('[data-side-next]')) return this.next(event.target.closest('[data-side-next]'));
        const option = event.target.closest('[data-option]');
        if (option) this.chooseOption(option);
      });

      this.root.addEventListener('input', event => {
        const field = event.target.closest('[data-field]');
        if (field) this.state.answers[field.dataset.field] = field.value;
        const custom = event.target.closest('[data-custom-for]');
        if (custom) {
          const group = custom.closest('[data-studio-step]')?.querySelector(`[data-multiple="${custom.dataset.customFor}"]`);
          const knownOptions = group ? optionsFrom(group).map(option => option.toLocaleLowerCase()) : [];
          this.state.answers[`${custom.dataset.customFor}_custom`] = custom.value
            .split(',')
            .map(value => value.trim())
            .filter(value => value && !knownOptions.includes(value.toLocaleLowerCase()))
            .join(', ');
        }
        if (custom) this.updateMultiConcierge(custom.closest('[data-studio-step]')?.querySelector('[data-multiple]'));
        this.save();
        this.updateNextState();
      });
    }

    chooseOption(button) {
      const single = button.closest('[data-single]');
      if (single) {
        single.querySelectorAll('[data-option]').forEach(item => item.setAttribute('aria-pressed', 'false'));
        button.setAttribute('aria-pressed', 'true');
        this.state.answers[single.dataset.single] = button.dataset.option;
        this.toggleSpecificSites();
        this.updateConcierge(single, button.dataset.option);
      }
      const multiple = button.closest('[data-multiple]');
      if (multiple) {
        const key = multiple.dataset.multiple;
        const selected = new Set([...multiple.querySelectorAll('[data-option][aria-pressed="true"]')].map(item => item.dataset.option));
        selected.has(button.dataset.option) ? selected.delete(button.dataset.option) : selected.add(button.dataset.option);
        this.state.answers[key] = [...selected];
        button.setAttribute('aria-pressed', String(selected.has(button.dataset.option)));
        const pairedField = multiple.closest('.scent-studio__field')?.querySelector(`[data-field="${key}"]`);
        if (pairedField) {
          const optionNames = optionsFrom(multiple).map(item => item.toLocaleLowerCase());
          const typed = pairedField.value.split(',').map(item => item.trim()).filter(item => item && !optionNames.includes(item.toLocaleLowerCase()));
          const merged = [...selected, ...typed].filter((item, index, items) => items.findIndex(value => value.toLocaleLowerCase() === item.toLocaleLowerCase()) === index);
          pairedField.value = merged.join(', ');
          this.state.answers[key] = pairedField.value;
        }
        const customField = multiple.closest('[data-studio-step]')?.querySelector(`[data-custom-for="${key}"]`);
        if (customField) {
          const customValue = this.state.answers[`${key}_custom`] || '';
          customField.value = [...selected, customValue].filter(Boolean).join(', ');
        }
        this.updateMultiConcierge(multiple);
      }
      this.save();
      this.updateNextState();
    }

updateMultiConcierge(group) {
      if (!group) return;
      const step = group.closest('[data-studio-step]');
      const output = step?.querySelector('[data-multi-concierge]');
      if (!output) return;
      const hasSelection = Boolean(group.querySelector('[data-option][aria-pressed="true"]'));
      const custom = step.querySelector('[data-custom-for]')?.value.trim();
      const response = output.dataset.response || '';
      output.textContent = hasSelection || custom ? response : '';
      output.classList.toggle('is-visible', Boolean((hasSelection || custom) && response));
    }

    updateConcierge(group, value) {
      const output = group.closest('[data-studio-step]')?.querySelector('[data-choice-concierge]');
      if (!output) return;
      const options = [...group.querySelectorAll('[data-option]')].map(button => button.dataset.option);
      const replies = (group.dataset.replies || '').split(/\r?\n|\|/).map(reply => reply.trim());
      const reply = replies[options.indexOf(value)] || '';
      output.textContent = reply;
      output.classList.toggle('is-visible', Boolean(reply));
    }

    toggleSpecificSites() {
      const wrapper = this.root.querySelector('[data-specific-sites]');
      if (!wrapper) return;
      const visible = this.state.answers.scope === this.config.selectSpecificLabel;
      wrapper.hidden = !visible;
      const input = wrapper.querySelector('[data-field="specific_sites"]');
      if (input) input.required = visible;
      if (!visible) {
        this.state.answers.specific_sites = '';
        if (input) input.value = '';
      }
    }

isFieldValid(field) {
      const value = field.value.trim();
      if (field.required && !value) return false;
      if (!value) return true;
      if (!field.checkValidity()) return false;
      if (field.matches('[data-phone]')) {
        const digits = value.replace(/\D/g, '');
        return digits.length >= 7 && digits.length <= 15;
      }
      if (field.matches('[data-website]')) {
        return /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}(\/[^\s]*)?$/i.test(value);
      }
      return true;
    }

    isComplete(step) {
      const requiredFields = [...step.querySelectorAll('[required]')];
      const fieldsComplete = requiredFields.every(field => this.isFieldValid(field));
      const optionalFieldsComplete = [...step.querySelectorAll('[data-website]')].every(field => this.isFieldValid(field));
      const requiredGroup = step.querySelector('[data-single][data-required="true"]');
      const groupComplete = !requiredGroup || Boolean(requiredGroup.querySelector('[data-option][aria-pressed="true"]'));
      return fieldsComplete && optionalFieldsComplete && groupComplete;
    }

    updateNextState() {
      const step = this.steps[this.state.index];
      if (!step) return;
      const incomplete = !this.isComplete(step);
      const nextButton = step.querySelector('[data-next]');
      if (nextButton) nextButton.disabled = incomplete;
      const sideNext = this.root.querySelector('[data-side-next]');
      if (sideNext) sideNext.disabled = incomplete || step.dataset.stepType === 'review' || step.dataset.stepType === 'success';
    }

    validate(step) {
      const message = step.querySelector('[data-message]');
      const requiredFields = [...step.querySelectorAll('[required]')];
      const validationFields = [...step.querySelectorAll('[required], [data-website]')];
      const invalidField = validationFields.find(field => !this.isFieldValid(field));
      const requiredGroup = step.querySelector('[data-single][data-required="true"]');
      const groupInvalid = requiredGroup && !requiredGroup.querySelector('[data-option][aria-pressed="true"]');
      if (invalidField || groupInvalid) {
        if (message) { message.textContent = this.config.requiredError; message.hidden = false; }
        invalidField?.focus();
        return false;
      }
      if (message) message.hidden = true;
      return true;
    }

skip() {
      const step = this.steps[this.state.index];
      if (step?.dataset.stepType === 'contact') {
        const message = step.querySelector('[data-message]');
        if (message) { message.textContent = this.config.requiredError; message.hidden = false; }
        return;
      }
      this.go(this.state.index + 1);
    }

    next(button) {
      const step = this.steps[this.state.index];
      if (!this.validate(step)) return;
      if (step.dataset.stepType === 'contact') this.state.answers.contact_step_skipped = 'no';
      if (step.dataset.stepType === 'review') return this.submit(button);
      this.go(this.state.index + 1);
    }

    go(index) {
      if (index < 0 || index >= this.steps.length) return;
      this.steps.forEach((step, stepIndex) => {
        const active = stepIndex === index;
        step.classList.toggle('is-active', active);
        step.setAttribute('aria-hidden', String(!active));
      });
      this.state.index = index;
      this.save();
      const step = this.steps[index];
      this.root.classList.toggle('is-cover-step', step.dataset.stepType === 'cover');
      this.root.classList.toggle('is-split-step', step.classList.contains('scent-studio__screen--split-right') || step.classList.contains('scent-studio__screen--split-left'));
      this.root.classList.toggle('is-success-step', step.dataset.stepType === 'success');
      const previousButton = this.root.querySelector('[data-side-prev]');
      const nextButton = this.root.querySelector('[data-side-next]');
      if (previousButton) previousButton.disabled = index <= 0;
      if (nextButton) nextButton.disabled = step.dataset.stepType === 'review' || step.dataset.stepType === 'success';
      const backButton = step.querySelector('[data-back]');
      if (backButton) backButton.style.visibility = index > 1 ? 'visible' : 'hidden';
      step.scrollTop = 0;
      if (step.dataset.stepType === 'review') this.renderReview(step);
      const progress = this.root.querySelector('[data-progress]');
      if (progress) progress.style.width = `${this.steps.length > 1 ? index / (this.steps.length - 1) * 100 : 100}%`;
      const pageCount = this.root.querySelector('[data-page-count]');
      if (pageCount) pageCount.textContent = `${String(index + 1).padStart(2, '0')} / ${String(this.steps.length).padStart(2, '0')}`;
      this.root.querySelector('[data-announcer]').textContent = `Step ${index + 1} of ${this.steps.length}`;
      requestAnimationFrame(() => step.querySelector('[data-step-heading]')?.focus({ preventScroll: true }));
      this.updateNextState();
      window.scrollTo({ top: this.root.offsetTop, behavior: 'smooth' });
    }

    flattenedAnswers() {
      return Object.fromEntries(Object.entries(this.state.answers).map(([key, value]) => [key, Array.isArray(value) ? value.join(', ') : value]));
    }

    renderReview(step) {
      const answers = this.flattenedAnswers();
      const combine = (values, separator = ' · ') => values.map(value => String(value || '').trim()).filter(Boolean).join(separator);
      const rows = [
        ['Business', combine([answers.business, answers.website])],
        ['Contact', combine([answers.contact, answers.email, answers.phone])],
        ['Account Manager', answers.account_manager],
        ['Applies to', combine([answers.scope, answers.specific_sites], ' — ')],
        ['Current scent', answers.current_scent],
        ['What to keep', answers.what_to_keep],
        ['What to change', answers.what_to_change],
        ['Reason for change', answers.reason_for_change],
        ['New direction', answers.new_direction],
        ['Notes drawn to', combine([answers.notes_drawn_to, answers.notes_drawn_to_custom])],
        ['Notes to avoid', combine([answers.notes_to_avoid, answers.notes_to_avoid_custom])],
        ['Intensity', answers.intensity],
        ['Anything else', answers.anything_else]
      ];
      step.querySelector('[data-review]').innerHTML = rows
        .map(([label, value]) => `<div class="scent-studio__review-row"><div class="scent-studio__review-key">${escapeHtml(label)}</div><div class="scent-studio__review-value ${value ? '' : 'is-empty'}">${escapeHtml(value || '—')}</div></div>`)
        .join('');
    }

    async submit(button) {
      const step = this.steps[this.state.index];
      const message = step.querySelector('[data-message]');
      const hasMeaningfulAnswer = Object.entries(this.state.answers).some(([key, value]) => {
        if (key === 'contact_step_skipped') return false;
        return Array.isArray(value) ? value.length > 0 : String(value || '').trim() !== '';
      });
      if (!hasMeaningfulAnswer) {
        message.textContent = 'Please complete at least one field or selection before submitting.';
        message.hidden = false;
        return;
      }
      const endpoint = this.root.dataset.endpoint;
      if (!endpoint) {
        message.textContent = this.config.submissionError;
        message.hidden = false;
        return;
      }
      button.disabled = true;
      const payload = {
        platform: this.config.source,
        submitted_at: new Date().toISOString(),
        shop_domain: window.Shopify?.shop || window.location.hostname,
        section_id: this.root.dataset.sectionId,
        ...this.flattenedAnswers()
      };
      payload.summary = Object.entries(payload).map(([key, value]) => `${key}: ${value}`).join('\n');
      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload)
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || result.ok === false) throw new Error(result.error || 'Submission failed');
        try { sessionStorage.removeItem(this.storageKey); } catch (_) {}
        const successIndex = this.steps.findIndex(item => item.dataset.stepType === 'success');
        this.go(successIndex >= 0 ? successIndex : this.state.index + 1);
      } catch (error) {
        message.textContent = this.config.submissionError;
        message.hidden = false;
        console.error('The Scent Studio submission:', error);
      } finally {
        button.disabled = false;
      }
    }

    destroy() {}
  }

  const instances = new WeakMap();
  const init = root => { if (root && !instances.has(root)) instances.set(root, new ScentStudio(root)); };
  document.querySelectorAll(selector).forEach(init);
  document.addEventListener('shopify:section:load', event => init(event.target.querySelector(selector)));
})();


